// domain/services/coverageGroups.js
import { COVERAGE_STATUS } from '../constants/coverage';
import { coverageStatus } from './coverageStatus';

/**
 * Splits a client's coverages on whether they can ever be billed again.
 *
 * A RULE, not a view. It happens to line up with the two lists the UI draws, but what it decides
 * is which coverages take part in the coordination-of-benefits sequence at all — and the unit of
 * work depends on that to hold ended coverages aside, so a reorder today never renumbers a plan
 * that stopped being billable last year. Moving this to the presentation layer would leave the
 * application layer depending on the UI for an invariant.
 *
 * `inSequence` is everything still in play, including coverage that has not started yet: a future
 * plan's place in the order is a real decision a biller can make now.
 *
 * `ended` is past its effective end date. It is retained rather than removed, because a service
 * date inside its period can still be billed against it, but it takes no part in today's ordering.
 *
 * Membership is derived, so a coverage moves between groups on its own as its end date passes —
 * nothing needs to be re-saved.
 */

/**
 * @param {import('../model/Coverage.types').Coverage[]} coverages
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {{ inSequence: import('../model/Coverage.types').Coverage[],
 *             ended: import('../model/Coverage.types').Coverage[] }}
 */
// eslint-disable-next-line import/prefer-default-export
export function partitionByLifecycle(coverages, asOf) {
  const live = coverages.filter((coverage) => coverage.isDeleted !== true);

  return {
    inSequence: live.filter((coverage) => coverageStatus(coverage, asOf) !== COVERAGE_STATUS.ENDED),
    ended: live.filter((coverage) => coverageStatus(coverage, asOf) === COVERAGE_STATUS.ENDED),
  };
} // partitionByLifecycle()
